import React, {useEffect, useState} from "react";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import PageHeader from "../../components/page-header";
import {CrmServiceConnectionApi} from "../../../models/crm/crm-api";
import {useTranslate} from "../../../system/useTranslate";
import {useWorkspaceRole} from "../../../system/use-workspace-role";

const crmConnectionApi = new CrmServiceConnectionApi();

function CrmConnection() {
    const translate = useTranslate();
    const {role} = useWorkspaceRole();
    const [status, setStatus] = useState(null);
    const [linkCode, setLinkCode] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    const loadStatus = async () => {
        setLoading(true);
        try {
            const response = await crmConnectionApi.getConnection();
            setStatus(response?.data || null);
        } catch (error) {
            setMessage({severity: "error", key: error?.message || "crm-connection.error.status"});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    const connect = async () => {
        const normalized = linkCode.trim();
        if (!/^zfn_link_[A-Za-z0-9_-]{43}$/.test(normalized)) {
            setMessage({severity: "error", key: "crm-connection.error.code-invalid"});
            return;
        }
        setSubmitting(true);
        setMessage(null);
        try {
            await crmConnectionApi.link(normalized);
            setLinkCode("");
            setMessage({severity: "success", key: "crm-connection.connected-success"});
            await loadStatus();
        } catch (error) {
            setMessage({severity: "error", key: error?.message || "crm-connection.error.link"});
        } finally {
            setSubmitting(false);
        }
    };

    if (role !== "owner") return null;

    return (
        <div className="flex flex-column p-4 gap-4 overflow-auto h-full">
            <PageHeader
                title="crm-connection"
                description="crm-connection-description"
                titleClassName="text-lg"
                descriptionClassName="text-xs"
            />
            <div className="surface-card border-1 surface-border border-round p-4 flex flex-column gap-3 w-full lg:w-8">
                <div className="flex align-items-center justify-content-between gap-3 flex-wrap">
                    <div className="flex flex-column gap-1">
                        <span className="font-semibold text-sm">{translate("crm-connection.workspace-link-title")}</span>
                        <small className="text-color-secondary text-xs line-height-3">{translate("crm-connection.workspace-link-description")}</small>
                    </div>
                    {!loading && (
                        <span className={`text-xs font-semibold ${status?.connected ? "text-green-600" : "text-orange-600"}`}>
                            {translate(status?.connected ? "crm-connection.status-connected" : "crm-connection.status-not-connected")}
                        </span>
                    )}
                </div>
                {message && <Message severity={message.severity} text={translate(message.key)} className="w-full text-xs" />}
                {!loading && status?.configured === false && (
                    <Message severity="warn" text={translate("crm-connection.not-configured")} className="w-full text-xs" />
                )}
                <div className="flex flex-column gap-2 border-top-1 surface-border pt-3">
                    <label htmlFor="crm-link-code" className="text-xs">{translate("crm-connection.code")}</label>
                    <div className="flex flex-column md:flex-row gap-2">
                        <InputText
                            id="crm-link-code"
                            value={linkCode}
                            onChange={(event) => setLinkCode(event.target.value)}
                            placeholder={translate("crm-connection.code-placeholder")}
                            className="flex-1 text-xs"
                            dir="ltr"
                            disabled={loading || submitting || status?.configured === false}
                        />
                        <Button
                            label={translate("crm-connection.connect")}
                            onClick={connect}
                            loading={submitting}
                            disabled={loading || submitting || !linkCode.trim() || status?.configured === false}
                            className="text-xs white-space-nowrap"
                        />
                    </div>
                    <small className="text-color-secondary text-xs">{translate("crm-connection.code-help")}</small>
                </div>
            </div>
        </div>
    );
}

export default CrmConnection;
